import type {
    ComputingChallengeStepTest,
    ComputingCodeMultiFile,
} from "#content-library/types/types.ts";

type Subscription = {
    remove: () => void;
};

type WorkerReadyMessage = {type: "WORKER_READY"};
type TestResultMessageData =
    | {
          status: "PASSED";
      }
    | {
          status: "FAILED" | "ERROR";
          failureMessage: string;
          failedTestName: string;
      };
type TestResultsMessage = {type: "TEST_RESULTS"} & TestResultMessageData;
type FocusedMessage = {type: "OUTPUT_FOCUSED"};
type BlurredMessage = {type: "OUTPUT_BLURRED"};
type CriticalErrorMessage = {type: "CRITICAL_ERROR"; message: string};

// Messages from sandbox iframe -> parent window
export type SandboxMessage =
    | BlurredMessage
    | CriticalErrorMessage
    | FocusedMessage
    | TestResultsMessage
    | WorkerReadyMessage;

/**
 * Send a message to the parent window indicating that the worker is ready to run Python code.
 */
export const sendWorkerReady = () => {
    sendMessageToParentWindow({type: "WORKER_READY"});
};

/**
 * Send a message to the parent window indicating that tests have completed.
 * @param data The result of the test run.
 */
export const sendTestResults = (data: TestResultMessageData) => {
    sendMessageToParentWindow({
        type: "TEST_RESULTS",
        ...data,
    });
};

/**
 * Send a message to the parent window indicating that there was a critical error
 * running the user's code.
 * @param message The error message to display to the user.
 */
export const sendCriticalError = (message: string) => {
    sendMessageToParentWindow({
        type: "CRITICAL_ERROR",
        message,
    });
};

/**
 * Send a message to the parent window indicating that the output panel has received focus.
 */
export const sendOutputFocused = () => {
    sendMessageToParentWindow({type: "OUTPUT_FOCUSED"});
};

/**
 * Send a message to the parent window indicating that focus has left the output panel.
 */
export const sendOutputBlurred = () => {
    sendMessageToParentWindow({type: "OUTPUT_BLURRED"});
};

const sendMessageToParentWindow = (message: SandboxMessage) => {
    window.parent.postMessage(message, "*");
};

/**
 * A helper function to subscribe to events coming from the sandbox iframe.
 * @param onMessage A callback that should handle all messages that can be
 * received from the sandbox iframe.
 * @returns A Subscription object that can be used to clean up the event listener.
 */
export const subscribeToEventsFromSandbox = (
    onMessage: (event: MessageEvent<SandboxMessage>) => void,
): Subscription => {
    window.addEventListener("message", onMessage);
    return {
        remove: () => {
            window.removeEventListener("message", onMessage);
        },
    };
};

type RunPythonCodeMessage = {
    type: "RUN_PYTHON_CODE";
    code: ComputingCodeMultiFile;
    tests?: Array<ComputingChallengeStepTest> | null;
};

type StopPythonCodeMessage = {
    type: "STOP_PYTHON_CODE";
};

// Events from parent window -> sandbox iframe
export type ParentMessage = RunPythonCodeMessage | StopPythonCodeMessage;

/**
 * Sends a message to the sandbox iframe to run the provided code and tests.
 * @param target The window to send the message to; this should be the content
 * window of the sandbox iframe.
 * @param code The code to run.
 * @param tests Optional - tests to run against the code (used for Challenges only)
 */
export const sendRunPythonCode = (
    target: Window | null | undefined,
    code: ComputingCodeMultiFile,
    tests?: Array<ComputingChallengeStepTest> | null,
) => {
    sendMessageToTargetWindow({type: "RUN_PYTHON_CODE", code, tests}, target);
};

const sendMessageToTargetWindow = (
    message: ParentMessage,
    target: Window | null | undefined,
) => {
    target?.postMessage(message, "*");
};

/**
 * Send a message to the iframe to stop execution.
 * @param target The window to send the message to; this should be the content window of the iframe.
 */
export const sendStopPythonCode = (target: Window | null | undefined) => {
    target?.postMessage({type: "STOP_PYTHON_CODE"}, "*");
};

/**
 * A helper function to subscribe to events from the parent window.
 * @param onMessage A callback that should handle all messages that can be received from the parent window.
 * @returns A Subscription object that can be used to clean up the event listener.
 */
export const subscribeToEventsFromParent = (
    onMessage: (event: MessageEvent<ParentMessage>) => void,
): Subscription => {
    window.addEventListener("message", onMessage);
    return {
        remove: () => {
            window.removeEventListener("message", onMessage);
        },
    };
};
